import { ChatIcon, EnvelopeIcon, PhoneIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { parseDate } from '@internationalized/date';
import { clone, difference, insert } from 'ramda';
import { v4 } from 'uuid';

import { ScheduleActivityConfig, ShiftTemplatesFormValues } from './types';
import { DayTemplate, QueueChannelType, ShiftBreak, ShiftTemplate, ShiftTemplateType } from '../../graphql/components';

export const scheduleActivitiesConfig: ScheduleActivityConfig[] = [
  {
    fieldPrefix: QueueChannelType.Voice.toLowerCase(),
    iconComponent: PhoneIcon,
    id: QueueChannelType.Voice,
    labelKey: `queue.channelType.${QueueChannelType.Voice}`,
  },
  {
    fieldPrefix: QueueChannelType.Email.toLowerCase(),
    iconComponent: EnvelopeIcon,
    id: QueueChannelType.Email,
    labelKey: `queue.channelType.${QueueChannelType.Email}`,
  },
  {
    fieldPrefix: QueueChannelType.Chat.toLowerCase(),
    iconComponent: ChatIcon,
    id: QueueChannelType.Chat,
    labelKey: `queue.channelType.${QueueChannelType.Chat}`,
  },
];

export const getInitialValues: (dayIndex?: number) => ShiftTemplatesFormValues = (dayIndex = 1) => ({
  type: ShiftTemplateType.AllWorkDays,
  effectiveDate: null,
  dayTemplates: [
    {
      dayIndex,
      breaks: [{ id: v4() } as ShiftBreak],
      scheduleActivitySettings: scheduleActivitiesConfig.map(({ id }) => ({
        id: v4(),
        type: id,
        minimumPerDay: 0,
        maximumPerDay: 0,
      })),
    } as DayTemplate,
  ],
});

export const initializeDayTemplates: (type?: ShiftTemplateType) => ShiftTemplatesFormValues['dayTemplates'] = (
  type
) => {
  if (type === ShiftTemplateType.EvenOdd) {
    return [...Array(14)].map((_, i) => ({
      ...getInitialValues(i + 1).dayTemplates[0],
    }));
  }

  return getInitialValues().dayTemplates;
};

export const formatValues = (shiftTemplate?: ShiftTemplate | null) => {
  if (!shiftTemplate) {
    return getInitialValues();
  }
  let dayTemplates = clone(shiftTemplate.dayTemplates);

  if (shiftTemplate.type === ShiftTemplateType.EvenOdd && dayTemplates.length !== 14) {
    // need to generate dayTemplate objects for all missing days
    const allDayTemplatesIndexes: number[] = [...Array(14)].map((_, i) => i + 1);
    const dayTemplatesIndexes = dayTemplates.map(({ dayIndex }) => dayIndex);
    const missingDayTemplatesIndexes = difference(allDayTemplatesIndexes, dayTemplatesIndexes);

    missingDayTemplatesIndexes.forEach((missingDayTemplateIndex) => {
      dayTemplates = insert(
        missingDayTemplateIndex - 1,
        getInitialValues(missingDayTemplateIndex).dayTemplates[0] as DayTemplate,
        dayTemplates
      );
    });
  }

  // Need to check existence of effectiveDate as in cloning scenario effectiveDate is deleted
  const effectiveDateAsDate = shiftTemplate?.effectiveDate ? parseDate(shiftTemplate.effectiveDate) : null;

  return {
    ...shiftTemplate,
    effectiveDate: effectiveDateAsDate,
    dayTemplates: dayTemplates.map((dayTemplate) => ({
      ...dayTemplate,
      breaks: dayTemplate.breaks.length ? dayTemplate.breaks : [{ id: v4() } as ShiftBreak],
      scheduleActivitySettings: scheduleActivitiesConfig.map((scheduleActivity) => {
        const matchingActivity = dayTemplate.scheduleActivitySettings.find(
          ({ type: activityType }) => activityType === scheduleActivity.id
        );

        return (
          matchingActivity ?? {
            id: v4(),
            type: scheduleActivity.id,
            minimumPerDay: 0,
            maximumPerDay: 0,
          }
        );
      }),
    })),
  } as ShiftTemplatesFormValues;
};
